/*
 * People Equation v2 — reader behaviour.
 *
 * Ported from dc/reader.js on feat/v3-design-pages. Two of the v3 file's five
 * behaviours are dropped here because the shared chrome bundle already owns
 * them: nav() (the header's scroll auto-hide + mobile menu live in chrome.js)
 * and newsletter() (every subscribe form is posted by chrome.js via the
 * data-nl-form hooks). What remains is reader-only and progressive: with this
 * file blocked the pages still read, still navigate, still link out to YouTube.
 *   - spine():     the contents rail + reading-progress crosshair.
 *   - share():     the "Copy link" control.
 *   - players():   the YouTube facade (no third-party request until Play).
 *   - guestNote():  the series "Read more" clamp toggle.
 */
(function () {
	"use strict";

	var root = document.documentElement;
	var reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	function each(list, fn) {
		Array.prototype.forEach.call(list || [], fn);
	}

	/* ── The spine ──────────────────────────────────────────────────────── */
	/*
	 * Progress is measured against the piece itself, not the document, so the
	 * crosshair reaches the bottom of the track when the reader reaches the end
	 * of the writing — not when they reach the end of the footer.
	 */

	function slug(text, used) {
		var base = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "section";
		var id = base;
		var n = 2;
		while (used[id]) {
			id = base + "-" + n++;
		}
		used[id] = true;
		return id;
	}

	/*
	 * The shallowest heading level the piece actually uses more than once, and
	 * only where that heading is a top-level child of the content. The depth
	 * restriction keeps figure-caption headings (columns labelled with an h4)
	 * out of the contents. Content wrapped in a group block has no top-level
	 * headings, so that case falls back to searching the whole subtree.
	 */
	function headingsOf(scope) {
		var levels = ["h2", "h3", "h4"];
		var i;

		for (i = 0; i < levels.length; i++) {
			var top = Array.prototype.filter.call(
				scope.querySelectorAll(":scope > " + levels[i]),
				function (h) { return h.previousElementSibling !== null; }
			);
			if (top.length > 1) {
				return top;
			}
		}

		for (i = 0; i < levels.length; i++) {
			var any = scope.querySelectorAll(levels[i]);
			if (any.length > 1) {
				return Array.prototype.slice.call(any);
			}
		}

		return [];
	}

	function spine() {
		var target = document.querySelector("[data-pe-piece]");
		if (!target) {
			return;
		}

		var track = document.querySelector("[data-pe-track]");
		var links = [];
		var marks = [];

		if (track && !track.hasAttribute("data-pe-track-fixed")) {
			var heads = headingsOf(target);
			var used = {};
			heads.forEach(function (h) {
				if (!h.id) {
					h.id = "pe-" + slug(h.textContent || "", used);
				}
				var a = document.createElement("a");
				a.href = "#" + h.id;
				a.textContent = (h.textContent || "").trim();
				track.appendChild(a);
				links.push(a);
				marks.push(h);
			});
			var label = document.querySelector("[data-pe-track-label]");
			if (label) {
				label.textContent = heads.length
					? (label.getAttribute("data-pe-track-label") || "In this article")
					: "Progress";
			}
		} else if (track) {
			// The podcast series page supplies its own ticks — one per episode.
			links = Array.prototype.slice.call(track.querySelectorAll("[data-pe-tick]"));
			marks = links.map(function (a) {
				return document.querySelector(a.getAttribute("href"));
			}).filter(Boolean);
		}

		var frame = null;
		function measure() {
			frame = null;

			var box = target.getBoundingClientRect();
			var top = window.scrollY + box.top;
			var span = target.offsetHeight;
			var line = window.scrollY + window.innerHeight * 0.34;
			var p = span > 0 ? (line - top) / span : 0;
			p = Math.max(0, Math.min(1, p));
			root.style.setProperty("--pe-progress", (p * 100).toFixed(2));

			var active = -1;
			for (var i = 0; i < marks.length; i++) {
				if (window.scrollY + marks[i].getBoundingClientRect().top <= line) {
					active = i;
				}
			}
			for (var j = 0; j < links.length; j++) {
				if (j === active) {
					links[j].setAttribute("data-on", "");
				} else {
					links[j].removeAttribute("data-on");
				}
			}
		}

		function schedule() {
			if (frame === null) {
				frame = window.requestAnimationFrame(measure);
			}
		}

		window.addEventListener("scroll", schedule, { passive: true });
		window.addEventListener("resize", schedule, { passive: true });
		measure();
	}

	/* ── Share ──────────────────────────────────────────────────────────── */

	function share() {
		each(document.querySelectorAll("[data-pe-copy]"), function (button) {
			// Update only the label span when present (icon variant), so the
			// leading SVG glyph survives the "Link copied" swap; fall back to the
			// whole button's text for the plain label variant.
			var label = button.querySelector("[data-pe-copy-label]") || button;
			var original = label.textContent;
			button.addEventListener("click", function () {
				var url = button.getAttribute("data-pe-copy") || window.location.href;
				var done = function () {
					label.textContent = "Link copied";
					button.setAttribute("data-copied", "");
					window.setTimeout(function () {
						label.textContent = original;
						button.removeAttribute("data-copied");
					}, 2200);
				};

				if (navigator.clipboard && navigator.clipboard.writeText) {
					navigator.clipboard.writeText(url).then(done, function () {
						window.prompt("Copy this link", url);
					});
					return;
				}

				var field = document.createElement("input");
				field.value = url;
				field.setAttribute("readonly", "");
				field.style.position = "fixed";
				field.style.opacity = "0";
				document.body.appendChild(field);
				field.select();
				try {
					document.execCommand("copy");
					done();
				} catch (e) {
					window.prompt("Copy this link", url);
				}
				document.body.removeChild(field);
			});
		});
	}

	/* ── Episode player ─────────────────────────────────────────────────── */
	/*
	 * The thumbnail is a facade. YouTube is contacted only once someone presses
	 * play, so opening an episode page costs no third-party request.
	 */

	function players() {
		each(document.querySelectorAll("[data-pe-yt]"), function (button) {
			button.addEventListener("click", function () {
				if (button.hasAttribute("data-playing")) {
					return;
				}
				var id = button.getAttribute("data-pe-yt");
				var frame = document.createElement("iframe");
				frame.src = "https://www.youtube-nocookie.com/embed/" + encodeURIComponent(id) +
					"?autoplay=1&rel=0&modestbranding=1";
				frame.title = button.getAttribute("data-pe-title") || "Episode video";
				frame.allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share";
				frame.setAttribute("allowfullscreen", "");
				button.setAttribute("data-playing", "");
				button.appendChild(frame);
			});
		});
	}

	/* ── Guest note ─────────────────────────────────────────────────────── */

	function guestNote() {
		each(document.querySelectorAll("[data-pe-more]"), function (button) {
			var body = document.getElementById(button.getAttribute("data-pe-more"));
			if (!body) {
				return;
			}
			// Only worth a control if there is more text than the clamp shows.
			if (body.scrollHeight <= body.clientHeight + 4) {
				button.hidden = true;
				body.removeAttribute("data-clamped");
				return;
			}
			button.addEventListener("click", function () {
				var open = !body.hasAttribute("data-clamped");
				if (open) {
					body.setAttribute("data-clamped", "");
					button.textContent = "Read more";
					button.setAttribute("aria-expanded", "false");
				} else {
					body.removeAttribute("data-clamped");
					button.textContent = "Read less";
					button.setAttribute("aria-expanded", "true");
				}
			});
		});
	}

	/* ── Deep-linked episode ────────────────────────────────────────────── */
	/*
	 * A /podcast/<episode-slug>/ URL renders the series page and marks the
	 * episode's 1-based position in data-pe-focus. Open that episode's
	 * description and bring it into view — no #fragment, the position is handed
	 * over by the server. Runs after guestNote() so the clamp state is settled.
	 */
	function focusEpisode() {
		var host = document.querySelector("[data-pe-focus]");
		if (!host) {
			return;
		}
		var n = parseInt(host.getAttribute("data-pe-focus"), 10);
		if (!n) {
			return;
		}
		var section = document.getElementById("pe-ep-" + n);
		if (!section) {
			return;
		}
		var body = document.getElementById("pe-ep-desc-" + n);
		var button = document.querySelector('[data-pe-more="pe-ep-desc-' + n + '"]');
		if (body && body.hasAttribute("data-clamped")) {
			body.removeAttribute("data-clamped");
			if (button) {
				button.hidden = false;
				button.textContent = "Read less";
				button.setAttribute("aria-expanded", "true");
			}
		}
		section.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
	}

	function boot() {
		spine();
		share();
		players();
		guestNote();
		focusEpisode();
		if (reduced) {
			root.classList.add("pe-reduced");
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot);
	} else {
		boot();
	}
}());
